/**
 * Thin client for the LexGat backend.
 *
 * Reads `VITE_API_BASE_URL` from the SPA env (no host hardcoded — switching
 * between local dev, staging, and production is a one-line .env change).
 * Attaches the current Firebase ID token to every request, throws typed
 * errors for non-2xx responses, and exposes a small SSE helper used by the
 * drafting endpoint's streaming variant.
 */

import { auth } from './firebase';
import { captureSentryException } from './sentry';

// Must use static `import.meta.env.VITE_*` — Vite only injects env when the key
// is a literal property access (casts / optional chaining break replacement).
const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const API_BASE = String(RAW_BASE).replace(/\/+$/, '');

function canUseRelativeApi(): boolean {
  return !API_BASE && typeof window !== 'undefined';
}

function backendReachabilityHint(): string {
  if (API_BASE) {
    return `Ensure the backend is running at ${API_BASE} (from the repo root: ./backend/scripts/dev.sh).`;
  }
  return (
    'Ensure the backend is running on port 8080 (./backend/scripts/dev.sh) and that the Vite dev ' +
    'server is proxying /api — leave VITE_API_BASE_URL empty in .env for local dev.'
  );
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class ApiNotConfiguredError extends ApiError {
  constructor() {
    super(
      'VITE_API_BASE_URL is not set. The AI features require the backend to be configured. ' +
        'Add VITE_API_BASE_URL=https://<backend-host> to your .env and restart `npm run dev`.',
      0,
      null,
    );
    this.name = 'ApiNotConfiguredError';
  }
}

/** Turn FastAPI / validation error bodies into a short user-facing message. */
export function formatApiErrorDetail(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (typeof body !== 'object') return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = Array.isArray((item as { loc?: unknown }).loc)
            ? (item as { loc: unknown[] }).loc.join('.')
            : '';
          return loc ? `${loc}: ${(item as { msg: string }).msg}` : (item as { msg: string }).msg;
        }
        return null;
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if ('message' in (body as object) && typeof (body as { message: unknown }).message === 'string') {
    return (body as { message: string }).message;
  }
  return null;
}

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('Not signed in', 401, null);
  }
  return await user.getIdToken();
}

function joinUrl(path: string): string {
  if (!API_BASE) {
    if (typeof window !== 'undefined') {
      return path.startsWith('/') ? path : `/${path}`;
    }
    throw new ApiNotConfiguredError();
  }
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

function mapFetchError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const detail =
    err instanceof Error && err.name === 'AbortError'
      ? 'The request was cancelled or timed out.'
      : `Cannot reach the LexGat backend. ${backendReachabilityHint()}`;
  return new ApiError(detail, 0, null);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { auth?: 'required' | 'optional' | 'none'; signal?: AbortSignal },
): Promise<T> {
  const mode = opts?.auth ?? 'required';
  let token: string | null = null;
  if (mode === 'required') token = await getIdToken();
  else if (mode === 'optional' && auth.currentUser) token = await getIdToken();
  let response: Response;
  try {
    response = await fetch(joinUrl(path), {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts?.signal,
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  let parsed: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const detail = formatApiErrorDetail(parsed) || `Request to ${path} failed`;
    if (response.status >= 500) {
      captureSentryException(new Error(`${method} ${path} failed`), {
        method,
        path,
        status: response.status,
        detail,
      });
    }
    throw new ApiError(detail, response.status, parsed);
  }
  return parsed as T;
}

async function postBlob(
  path: string,
  body: unknown,
  init?: { signal?: AbortSignal },
): Promise<Blob> {
  const token = await getIdToken();
  let response: Response;
  try {
    response = await fetch(joinUrl(path), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept:
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      },
      body: JSON.stringify(body),
      signal: init?.signal,
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  if (!response.ok) {
    const text = await response.text();
    let parsed: unknown = text;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    const detail = formatApiErrorDetail(parsed) || `Request to ${path} failed`;
    if (response.status >= 500) {
      captureSentryException(new Error(`POST ${path} failed`), {
        method: 'POST',
        path,
        status: response.status,
        detail,
      });
    }
    throw new ApiError(detail, response.status, parsed);
  }

  return await response.blob();
}

/** POST once; on 5xx, wait 1.5s and retry once (transactional email endpoints). */
async function postWithServerRetry<T>(path: string, body?: unknown): Promise<T> {
  try {
    return await request<T>('POST', path, body);
  } catch (err) {
    if (err instanceof ApiError && err.status >= 500) {
      await new Promise((r) => setTimeout(r, 1500));
      return await request<T>('POST', path, body);
    }
    throw err;
  }
}

export const api = {
  isConfigured(): boolean {
    return Boolean(API_BASE) || canUseRelativeApi();
  },
  baseUrl(): string {
    return API_BASE;
  },
  get<T>(path: string): Promise<T> {
    return request<T>('GET', path);
  },
  post<T>(path: string, body?: unknown, init?: { signal?: AbortSignal }): Promise<T> {
    return request<T>('POST', path, body, { signal: init?.signal });
  },
  postBlob(path: string, body?: unknown, init?: { signal?: AbortSignal }): Promise<Blob> {
    return postBlob(path, body, init);
  },
  postWithServerRetry<T>(path: string, body?: unknown): Promise<T> {
    return postWithServerRetry<T>(path, body);
  },
  getPublic<T>(path: string): Promise<T> {
    return request<T>('GET', path, undefined, { auth: 'none' });
  },
  postPublic<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('POST', path, body, { auth: 'none' });
  },
  /**
   * Open a Server-Sent Events stream against the backend.
   * Yields parsed JSON payloads tagged with their event name.
   */
  async *stream<T = unknown>(
    path: string,
    body: unknown,
    init?: { signal?: AbortSignal },
  ): AsyncGenerator<{ event: string; data: T }> {
    const token = await getIdToken();
    let response: Response;
    try {
      response = await fetch(joinUrl(path), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal: init?.signal,
      });
    } catch (err) {
      throw mapFetchError(err);
    }
    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      let parsed: unknown = text;
      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = text;
        }
      }
      const detail = formatApiErrorDetail(parsed) || text || 'Stream request failed';
      if (response.status >= 500) {
        captureSentryException(new Error(`POST ${path} stream failed`), {
          method: 'POST',
          path,
          status: response.status,
        });
      }
      throw new ApiError(detail, response.status, parsed);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const aborted = () => init?.signal?.aborted === true;

    try {
      while (true) {
        if (aborted()) break;
        let readResult: ReadableStreamReadResult<Uint8Array>;
        try {
          readResult = await reader.read();
        } catch (err) {
          if (
            aborted() ||
            (err instanceof DOMException && err.name === 'AbortError')
          ) {
            break;
          }
          throw err;
        }
        const { done, value } = readResult;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const drained = drainSseBuffer(buffer);
        buffer = drained.remainder;
        for (const ev of drained.events) {
          if (aborted()) return;
          yield ev as { event: string; data: T };
        }
      }
      if (aborted()) return;
      buffer += decoder.decode();
      const drained = drainSseBuffer(buffer);
      for (const ev of drained.events) {
        if (aborted()) return;
        yield ev as { event: string; data: T };
      }
      const tail = drained.remainder.trim();
      if (tail && !aborted()) {
        const ev = parseSseChunk(tail);
        if (ev) yield ev as { event: string; data: T };
      }
    } finally {
      try {
        await reader.cancel();
      } catch {
        // Ignore cancel errors during teardown.
      }
      reader.releaseLock();
    }
  },
};

/** Normalize CRLF so event boundaries are found with \\n\\n only. */
function normalizeSseBuffer(buffer: string): string {
  return buffer.replace(/\r\n/g, '\n');
}

/** Split complete SSE messages on blank line (\\n\\n+), per the SSE spec. */
function splitOnDoubleNewline(buffer: string): { complete: string[]; remainder: string } {
  const normalized = normalizeSseBuffer(buffer);
  const parts = normalized.split(/\n\n+/);
  const remainder = parts.pop() ?? '';
  const complete = parts.map((p) => p.trim()).filter(Boolean);
  return { complete, remainder };
}

/**
 * Fallback when the server omits the blank line between events: split before each
 * new `event:` line so multiple events in one buffer are not merged.
 */
function splitOnEventLineBoundaries(text: string): { complete: string[]; remainder: string } {
  const normalized = normalizeSseBuffer(text).trim();
  if (!normalized) return { complete: [], remainder: '' };

  const eventStarts: number[] = [];
  const re = /^event:/gm;
  let match: RegExpExecArray | null;
  while ((match = re.exec(normalized)) !== null) {
    eventStarts.push(match.index);
  }
  if (eventStarts.length <= 1) {
    return { complete: [], remainder: normalized };
  }

  const complete: string[] = [];
  for (let i = 0; i < eventStarts.length - 1; i++) {
    const block = normalized.slice(eventStarts[i], eventStarts[i + 1]).trim();
    if (block) complete.push(block);
  }
  const remainder = normalized.slice(eventStarts[eventStarts.length - 1]).trim();
  return { complete, remainder };
}

function drainSseBuffer(buffer: string): {
  events: { event: string; data: unknown }[];
  remainder: string;
} {
  const events: { event: string; data: unknown }[] = [];

  let { complete, remainder } = splitOnDoubleNewline(buffer);
  for (const block of complete) {
    const ev = parseSseChunk(block);
    if (ev) events.push(ev);
  }

  const merged = splitOnEventLineBoundaries(remainder);
  for (const block of merged.complete) {
    const ev = parseSseChunk(block);
    if (ev) events.push(ev);
  }
  remainder = merged.remainder;

  return { events, remainder };
}

/** Parse one SSE message block (lines between \\n\\n boundaries). */
function parseSseChunk(chunk: string): { event: string; data: unknown } | null {
  const lines = normalizeSseBuffer(chunk).split('\n');
  let event = 'message';
  let dataLines: string[] = [];
  for (const line of lines) {
    if (!line) continue;
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.replace(/^data:\s?/, ''));
    }
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  try {
    return { event, data: JSON.parse(raw) };
  } catch {
    return { event, data: raw };
  }
}
